import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { fetchAgentPipeline, updateAgentPipeline } from "api/axios";
import PipelineConfiguration from "components/pipeline/pipeline-configuration";
import { PipelineItem } from "../../new-agent/contexts/NewAgentContext";
import { useParams } from "react-router-dom";
import { useAgentInfo } from "layouts/agent/agent-info/_context/AgentInfoContext";

const Pipeline = () => {
  const { t } = useTranslation();
  const { agentInfo } = useAgentInfo();
  const params = useParams();
  const agentId = useMemo(
    () => agentInfo?.id ?? params?.id ?? null,
    [agentInfo?.id, params?.id]
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [pipelines, setPipelines] = useState<PipelineItem[]>([]);
  const [originalPipelines, setOriginalPipelines] = useState<PipelineItem[]>(
    []
  );

  useEffect(() => {
    if (!agentId) {
      setLoading(false);
      setError(
        t("agents.pipeline.missingAgent", {
          defaultValue: "Agent information is missing.",
        })
      );
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        const pipelineResponse = await fetchAgentPipeline(agentId);
        const pipelinesData = pipelineResponse?.pipelines ?? [];

        const mappedPipelines: PipelineItem[] = pipelinesData.map(
          (pipelineStep) => ({
            input: pipelineStep.input ?? "",
            agent_id: pipelineStep.agent_id ?? "",
            output: pipelineStep.output ?? "",
          })
        );

        const initialPipelines = JSON.parse(
          JSON.stringify(mappedPipelines)
        ) as PipelineItem[];
        setPipelines(initialPipelines);
        setOriginalPipelines(
          JSON.parse(JSON.stringify(mappedPipelines)) as PipelineItem[]
        );
        setError(null);
      } catch (err) {
        setError(t("agents.assignedSpecialists.failedToFetchSpecialists"));
        setPipelines([]);
        setOriginalPipelines([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [agentId, t]);

  const handleSave = async () => {
    if (isSaving) {
      return;
    }

    if (!isPipelineValid) {
      toast.error(
        t("agents.pipeline.validationError", {
          defaultValue:
            "Please add at least one pipeline step and fill agent, input, and output for each step before saving.",
        })
      );
      return;
    }

    if (!agentId) {
      toast.error(
        t("agents.pipeline.missingAgent", {
          defaultValue: "Agent information is missing.",
        })
      );
      return;
    }

    setIsSaving(true);

    try {
      const payload = {
        pipelines: pipelines.map((pipeline) => ({
          agent_id: pipeline.agent_id,
          input: pipeline.input,
          output: pipeline.output,
        })),
      };

      await updateAgentPipeline(agentId, payload);
      const cloned = JSON.parse(JSON.stringify(pipelines)) as PipelineItem[];
      setOriginalPipelines(cloned);
      toast.success(
        t("agents.pipeline.updateSuccess", {
          defaultValue: "Pipeline updated successfully",
        })
      );
    } catch (err) {
      console.error("Failed to update pipeline:", err);
      toast.error(
        t("agents.pipeline.updateFailed", {
          defaultValue: "Failed to update pipeline",
        })
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setPipelines(JSON.parse(JSON.stringify(originalPipelines)));
  };

  const hasChanges = useMemo(() => {
    return JSON.stringify(pipelines) !== JSON.stringify(originalPipelines);
  }, [pipelines, originalPipelines]);

  const isPipelineValid = useMemo(() => {
    if (!pipelines.length) {
      return false;
    }

    return pipelines.every((pipeline) => {
      const agentId = pipeline.agent_id?.toString().trim();
      const input = pipeline.input?.toString().trim();
      const output = pipeline.output?.toString().trim();

      return Boolean(agentId && input && output);
    });
  }, [pipelines]);

  if (loading) {
    return (
      <div className="rounded-lg bg-white p-8 shadow-sm dark:bg-gray-800">
        <div className="text-center">
          {t("agents.assignedSpecialists.loadingSpecialists")}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg bg-white p-8 shadow-sm dark:bg-gray-800">
        <div className="text-center text-red-500">{error}</div>
      </div>
    );
  }

  return (
    <>
      <PipelineConfiguration
        value={pipelines}
        onChange={setPipelines}
        showLabel={false}
        className="mt-4"
      />

      <div className="mt-6 flex justify-end gap-3 border-t border-gray-200 pt-6 dark:border-gray-700">
        <button
          type="button"
          onClick={handleCancel}
          disabled={!hasChanges || isSaving}
          className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-70 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
        >
          {t("common.cancel", { defaultValue: "Cancel" })}
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={!hasChanges || isSaving || !isPipelineValid}
          className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-600 disabled:cursor-not-allowed disabled:bg-brand-300 disabled:opacity-70 dark:bg-brand-400 dark:hover:bg-brand-500 dark:disabled:bg-brand-300"
        >
          {isSaving
            ? t("common.saving", { defaultValue: "Saving..." })
            : t("agents.pipeline.saveButton")}
        </button>
      </div>
    </>
  );
};

export default Pipeline;
